import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import _ from 'lodash'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import usersCollection from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import { useUpdateEmployee } from 'modules/employees/hooks/useUpdateEmployee'
import { getDirtyValues } from 'modules/employees/lib/getDirtyValues'
import employeesCollection from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import taxResidences from 'modules/payroll_bundle/store/collections/taxResidences'
import type TaxResidence from 'modules/payroll_bundle/store/models/TaxResidence'

import FormSection from '../FormSection'
import ResidenceSection from './ResidenceSection'
import { ResidenceValues } from './ResidenceValues'
import TaxResidenceSection from './TaxResidenceSection'

interface Props {
  employee: Employee
  canEdit: boolean
}
const ResidenceInformation: React.FC<Props> = ({ employee, canEdit }) => {
  const legalEntity = employeeLegalEntity(employee)
  const isSpainLE = legalEntity.country?.toLowerCase() === 'es'
  const showTaxResidence =
    (employee.isEligibleForPayroll || hasFeature('payroll_bundle')) && isSpainLE
  const { update } = useUpdateEmployee()
  const employeeTaxResidence = taxResidences.find({
    employee_id: employee.attributes.id,
  })

  const { loading } = useAsyncData(
    () =>
      Promise.all([
        usersCollection.fetch(),
        employeesCollection.fetch(),
        taxResidences.fetch({
          data: { employee_id: employee.id },
        }),
      ]),
    []
  )

  const toCamelCase = (data: TaxResidence | null | undefined) => {
    return _.mapKeys(data?.attributes, (value, key) => _.camelCase(key))
  }

  const form = useForm<ResidenceValues>({
    defaultValues: {
      addressLine1: employee.attributes.address_line_1 ?? '',
      addressLine2: employee.attributes.address_line_2 ?? '',
      city: employee.attributes.city ?? '',
      country: employee.attributes.country ?? '',
      postalCode: employee.attributes.postal_code ?? '',
      state: employee.attributes.state ?? '',
      taxResidence: employeeTaxResidence,
    },
    validate(data) {
      const errors: FormErrors<ResidenceValues> = {}

      if (!data.addressLine1) {
        errors.addressLine1 = i18n.t('employee_form.validations.empty')
      }

      if (!data.city) {
        errors.city = i18n.t('employee_form.validations.empty')
      }

      if (!data.country) {
        errors.country = i18n.t('employee_form.validations.empty')
      }

      if (!data.postalCode) {
        errors.postalCode = i18n.t('employee_form.validations.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      track(trackingEvents.employee.editEmployeeInformationSubmit, {
        fields_edited: [
          ...getDirtyValues(
            Object.entries(form.values),
            Object.fromEntries(Object.entries(form.initialValues))
          ),
        ],
        section_edited: 'residence_information',
        legal_entity_country: legalEntity.country,
        layout_version: 'v2',
      })

      const result = await update({
        ...data,
        id: employee.id,
        accessId: employee.attributes.access_id,
        gqlAddressLine1: data.addressLine1,
        gqlAddressLine2: data.addressLine2,
        taxResidence: taxResidenceInfo(data.taxResidence),
      })

      const handled = handleResult(result, (errors) => {
        setErrors({ ...errors })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        const values = form.values
        form.resetForm({ values })
      }

      return handled
    },
  })

  const taxResidenceInfo = (values: TaxResidence | null | undefined) => {
    if (!values) return undefined

    return {
      ...toCamelCase(values),
      employeeId: employee.id,
      id: values.attributes.id,
      isResident: values.attributes.is_resident,
    }
  }

  const handleFieldChanged = (
    fieldName: string,
    fieldValue: string | undefined | Record<string, unknown>
  ) => {
    form.setValues({ ...form.values, [fieldName]: fieldValue })
  }

  return (
    <FormSection
      title={i18n.t('employee_form.residence_section.title')}
      description={i18n.t('employee_form.residence_section.description')}
      illustration="location-building-pin"
      disabled={!form.isValid || !form.dirty}
      submitAction={() => form.handleSubmit()}
    >
      {(!!loading || form.isSubmitting) && <Loading />}
      {!loading && !form.isSubmitting && (
        <Box gap="s24">
          <ResidenceSection
            values={form.values}
            onChangeValue={(fieldName, fieldValue) => handleFieldChanged(fieldName, fieldValue)}
            readonly={!canEdit}
            errors={form.errors}
          />
          {showTaxResidence && (
            <TaxResidenceSection
              values={form.values}
              onChangeValue={(fieldName, fieldValue) => handleFieldChanged(fieldName, fieldValue)}
              readonly={!canEdit}
            />
          )}
        </Box>
      )}
    </FormSection>
  )
}

export default ResidenceInformation
